<template>
    <MainContent
        title="Listado de indumentarias"
        :value="filters"
        :meta="indumentarias.meta"
        @input="val => Object.assign(filters, val)"
        @updated="debouncedFetch"
    >
        <template #header>
            <Card customClasses="tw:mb-4">
                <div class="tw:flex tw:flex-col tw:gap-4 tw:w-full">
                    <!-- Action buttons row -->
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:items-center tw:gap-3 tw:justify-between">
                        <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:w-full tw:md:w-auto">
                            <Button
                                class="tw:w-full tw:md:w-auto"
                                as="router-link"
                                :to="{ name: 'back.bases.indumentarias.crear', params: { lista_id: listaId } }"
                                icon="fas fa-plus"
                                customClass="tw:text-xs tw:sm:text-sm tw:justify-center"
                            >
                                Nueva indumentaria
                            </Button>
                            <Button
                                class="tw:w-full tw:md:w-auto"
                                as="router-link"
                                :to="{ name: 'back.bases.indumentarias.inventario', params: { lista_id: listaId } }"
                                variant="ghost"
                                icon="fas fa-boxes"
                                customClass="tw:text-xs tw:sm:text-sm tw:border tw:border-gray-300 tw:justify-center"
                            >
                                Inventario
                            </Button>
                        </div>
                    </div>

                    <div class="tw:border-t tw:border-gray-200"></div>

                    <!-- Filters -->
                    <div class="tw:flex tw:flex-col tw:sm:flex-row tw:gap-4">
                        <div class="tw:flex-1">
                            <SearchComponent
                                :value="filters.search"
                                placeholder="Buscar"
                                @input="val => filters.search = val"
                            />
                        </div>

                        <div class="tw:w-full tw:sm:w-48" v-if="tallas.length">
                            <BaseMultiselect
                                :value="filters.talla || '__all__'"
                                :options="tallaFilterOptions"
                                value-key="value"
                                label-key="label"
                                label="Filtrar talla"
                                :multiple="false"
                                @input="val => { filters.talla = val === '__all__' ? '' : (val || ''); }"
                            />
                        </div>
                    </div>
                </div>
            </Card>
        </template>

        <!-- Table -->
        <BaseTable
            :columns="columns"
            :data="indumentarias.data || []"
            :skeleton="loading"
            :skeleton-rows="5"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-id>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-6"></div>
            </template>
            <template #skeleton-header-nombre>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20"></div>
            </template>
            <template #skeleton-header-obligatoria>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-tallas>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-precio>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-imagen>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-12 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-incluida>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-cantidad_incluida>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-20 tw:mx-auto"></div>
            </template>
            <template #skeleton-header-opciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-26 tw:mx-auto"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-id>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-8"></div>
            </template>
            <template #skeleton-nombre>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-obligatoria>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>
            <template #skeleton-tallas>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-22 tw:mx-auto"></div>
            </template>
            <template #skeleton-precio>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-16 tw:mx-auto"></div>
            </template>
            <template #skeleton-imagen>
                <div class="tw:h-16 tw:w-16 tw:bg-gray-200 tw:rounded"></div>
            </template>
            <template #skeleton-incluida>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>
            <template #skeleton-cantidad_incluida>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-6 tw:mx-auto"></div>
            </template>
            <template #skeleton-opciones>
                <div class="tw:h-9 tw:w-28 tw:bg-gray-200 tw:rounded tw:mx-auto"></div>
            </template>

            <template #cell-tallas="{ row }">
                <span v-if="row.tallasSelect && row.tallasSelect.length">
                    {{ row.tallasSelect.map(t => t.nombre).join(', ') }}
                </span>
                <span v-else class="tw:text-gray-400">—</span>
            </template>

            <template #cell-precio="{ row }">
                <span v-if="row.incluida">
                    <del class="tw:text-gray-400">{{ formatPrice(row.precio) }}</del>
                    <span class="tw:text-green-600 tw:ml-1">(0 €)</span>
                </span>
                <span v-else>{{ formatPrice(row.precio) }}</span>
            </template>

            <template #cell-imagen="{ row }">
                <img v-if="row.imagen_formatted || row.imagen" :src="row.imagen_formatted || row.imagen" class="tw:w-16 tw:h-16 tw:object-cover tw:rounded" />
                <span v-else class="tw:text-gray-400">—</span>
            </template>

            <template #cell-incluida="{ row }">
                <span v-if="row.incluida" class="tw:text-green-600">
                    <i class="fas fa-check-circle"></i>
                </span>
                <span v-else class="tw:text-red-500">
                    <i class="fas fa-times-circle"></i>
                </span>
            </template>

            <template #cell-obligatoria="{ row }">
                <span v-if="row.obligatoria" class="tw:text-green-600">
                    <i class="fas fa-check-circle"></i>
                </span>
                <span v-else class="tw:text-red-500">
                    <i class="fas fa-times-circle"></i>
                </span>
            </template>

            <template #cell-cantidad_incluida="{ row }">
                <span v-if="row.incluida || row.cantidad_incluida > 0">{{ row.cantidad_incluida }}</span>
                <span v-else class="tw:text-gray-400">—</span>
            </template>

            <template #cell-opciones="{ row, index }">
                <OpcionesDropdownTableComponent
                    :lastIndex="(indumentarias.data || []).length > 3 && index >= (indumentarias.data || []).length - 3"
                    :options="getRowOptions(row)"
                />
                <BorrarModalComponent
                    :name="'borrar-' + row.id"
                    :title="'Borrar indumentaria ' + row.nombre"
                    @close="() => {}"
                    @confirm="() => borrar(row.id)"
                >
                    ¿Está segur@ de borrar esta indumentaria?
                </BorrarModalComponent>
            </template>
        </BaseTable>

        <!-- Skeleton footer (solo primera carga) -->
        <template v-if="!hasLoadedOnce && loading">
            <Card :custom-classes="'tw:flex tw:flex-col tw:items-center tw:justify-between tw:gap-2 tw:pl-4 tw:pr-4 tw:py-2 tw:mt-6'">
                <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                    <template #skeleton>
                        <div class="tw:flex tw:flex-col tw:w-full tw:md:flex-row tw:md:items-center tw:md:justify-between">
                            <div class="tw:flex tw:justify-between tw:items-center tw:gap-4">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-18 tw:rounded tw:md:tw:hidden" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-20 tw:rounded-lg" />
                            </div>
                            <div class="tw:hidden tw:md:block">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-48 tw:rounded tw:mx-auto" />
                            </div>
                            <div class="tw:flex tw:justify-center tw:items-center tw:gap-1">
                                <SkeletonItem v-for="n in 3" :key="'pag-sk-' + n" height="tw:h-8" customClass="tw:w-8 tw:rounded" />
                            </div>
                        </div>
                    </template>
                </Skeleton>
            </Card>
        </template>
    </MainContent>
</template>

<script setup>
    import { computed, ref, watch, getCurrentInstance } from 'vue';

    defineProps({
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    });

    import MainContent from '@/modules/admin/ui/MainContent.vue';
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue';
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue';
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton';

    import { useIndumentariasIndex } from '@/composables/admin/bases/useIndumentariasIndex';
    import { useFormatPrice } from '@/composables/common/useFormatPrice';

    const { proxy } = getCurrentInstance();
    const formatPrice = useFormatPrice();

    const {
        indumentarias,
        loading,
        tallas,
        filters,
        listaId,
        fetch,
        borrar,
        debouncedFetch,
    } = useIndumentariasIndex();

    const hasLoadedOnce = ref(false);
    watch(indumentarias, val => {
        if (val?.data?.length) hasLoadedOnce.value = true;
    });

    const columns = [
        { key: 'id', label: 'ID', class: 'tw:w-14' },
        { key: 'nombre', label: 'Nombre', class: 'tw:w-44 ' },
        { key: 'obligatoria', label: 'Obligatoria', class: 'tw:w-24', centered: true },
        { key: 'tallas', label: 'Tallas', class: 'tw:w-28 ', centered: true },
        { key: 'precio', label: 'Precio', class: 'tw:w-24', centered: true },
        { key: 'imagen', label: 'Imagen', class: 'tw:w-28', centered: true },
        { key: 'incluida', label: 'Incluida', class: 'tw:w-24', centered: true },
        { key: 'cantidad_incluida', label: 'Cantidad incl.', class: 'tw:w-24', centered: true },
        { key: 'opciones', label: 'Opciones', class: 'tw:w-32', centered: true },
    ];

    function getRowOptions(row) {
        return [
            {
                to: { name: 'back.bases.indumentarias.show', params: { lista_id: listaId.value, indumentaria_id: row.id } },
                label: 'Detalle',
                icon: 'eye',
            },
            {
                to: { name: 'back.bases.indumentarias.update', params: { lista_id: listaId.value, indumentaria_id: row.id } },
                label: 'Editar',
            },
            {
                type: 'action',
                action: () => showDeleteModal(row.id),
                label: 'Borrar',
            },
        ];
    }

    function showDeleteModal(id) {
        proxy.$modal.show('borrar-' + id);
    }

    const tallaFilterOptions = computed(() => {
        const options = [{ value: '__all__', label: 'Todas las tallas' }];
        for (const t of tallas.value) {
            options.push({ value: t.nombre, label: t.nombre });
        }
        return options;
    });
</script>
